/**
 * useApi Hook
 *
 * A thin wrapper around SolidJS createResource for API calls.
 * Provides consistent loading, error, and data states.
 *
 * Usage:
 * ```typescript
 * const { data, loading, error, refetch } = useApi(() =>
 *   api.get<WorkItem[]>('/work-items')
 * );
 * ```
 *
 * With reactive source:
 * ```typescript
 * const [projectId, setProjectId] = createSignal('123');
 * const { data } = useApiWithSource(
 *   projectId,
 *   (id) => api.get<WorkItem[]>(`/projects/${id}/work-items`)
 * );
 * ```
 */

import { createResource, Accessor, ResourceReturn } from "solid-js";

/**
 * Return type for useApi hook
 */
export interface UseApiResult<T> {
  /** The fetched data (undefined while loading or on error) */
  data: Accessor<T | undefined>;
  /** Whether the request is currently loading */
  loading: Accessor<boolean>;
  /** Error object if the request failed */
  error: Accessor<Error | undefined>;
  /** Refetch the data */
  refetch: ResourceReturn<T, unknown>[1]["refetch"];
  /** Mutate the cached data */
  mutate: ResourceReturn<T, unknown>[1]["mutate"];
}

/**
 * Simple API hook using createResource under the hood.
 *
 * For simple fetches without a reactive source:
 * ```typescript
 * const { data, loading, error } = useApi(() => api.get<User>('/me'));
 * ```
 */
export function useApi<T>(fetcher: () => Promise<T>): UseApiResult<T> {
  const [resource, { refetch, mutate }] = createResource(fetcher);

  return {
    data: () => resource(),
    loading: () => resource.loading,
    error: () => resource.error,
    refetch,
    mutate,
  };
}

/**
 * API hook with a reactive source.
 *
 * Use this when the fetch depends on a reactive value:
 * ```typescript
 * const [id] = createSignal('123');
 * const { data } = useApiWithSource(id, (id) => api.get<Item>(`/items/${id}`));
 * ```
 */
export function useApiWithSource<T, S>(
  source: Accessor<S>,
  fetcher: (source: S) => Promise<T>,
): UseApiResult<T> {
  const [resource, { refetch, mutate }] = createResource(source, fetcher);

  return {
    data: () => resource(),
    loading: () => resource.loading,
    error: () => resource.error,
    refetch,
    mutate,
  };
}
